// frontend/src/components/Requisitions/edit/utils.js

import { STATUS_LABEL } from "./constants";

/* ───────────────────────────── Estatus helpers ───────────────────────────── */

export function displayStatus(val) {
  return STATUS_LABEL.get(String(val || "").trim().toLowerCase()) ?? (val || "—");
}

/* ───────────────────────────── Catálogo helpers ──────────────────────────── */

export function existsInCatalog(list, id) {
  if (!id) return true;
  return (list || []).some((x) => String(x.id) === String(id));
}

/* ───────────────────────────── Normalizers ───────────────────────────────── */

export function getLabelDefault(row) {
  const code = (row?.code ?? "").toString().trim();
  const text = (row?.name ?? row?.description ?? "").toString().trim();

  if (code && text) return `${code} - ${text}`;
  if (text) return text;

  return code || String(row?.id ?? "");
}

export function coerceId(value) {
  return typeof value === "object" && value ? value.id : Number(value || "") || "";
}

export function extractPrimitiveId(value) {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "object") {
    return String(value.id ?? value.pk ?? value.uuid ?? "");
  }
  return String(value);
}

export function normalizeId(value) {
  const raw = value && typeof value === "object"
    ? value.id ?? value.pk ?? value.uuid
    : value;

  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function getAdministrativeUnitRefFromRequisition(rawRequisition) {
  return (
    normalizeId(rawRequisition?.administrative_unit_ref) ??
    normalizeId(rawRequisition?.administrative_unit?.id) ??
    normalizeId(rawRequisition?.administrative_unit) ??
    null
  );
}

export function getLockedProductIdFromItems(items = []) {
  for (const item of items) {
    const productId = normalizeId(item?.product);
    if (productId) return productId;
  }
  return null;
}

export function getProductLabel(row) {
  const explicit = (row?.label ?? "").toString().trim();
  if (explicit) return explicit;

  const code = (row?.code ?? "").toString().trim();
  const desc = (row?.description ?? row?.name ?? "").toString().trim();

  if (code && desc) return `${code} - ${desc}`;
  return desc || code || String(row?.id ?? "");
}

export function getUnitLabel(row) {
  const explicit = (row?.label ?? row?.unit_display ?? "").toString().trim();
  if (explicit) return explicit;

  const code = (row?.code ?? "").toString().trim();
  const name = (row?.name ?? row?.description ?? "").toString().trim();

  if (code && name) return `${code} - ${name}`;
  return name || code || String(row?.id ?? "");
}

export function normalizeProductRow(row) {
  const id = extractPrimitiveId(row?.id ?? row?.pk ?? row?.uuid);
  const code = (row?.code ?? row?.clave ?? row?.codigo ?? "").toString().trim();
  const description = (row?.description ?? row?.name ?? row?.nombre ?? row?.label ?? "").toString().trim();

  return {
    id,
    code,
    description,
    name: description,
    label: getProductLabel({ code, description, label: row?.label }),
  };
}

export function normalizeUnitRow(row) {
  const id = extractPrimitiveId(row?.id ?? row?.pk ?? row?.uuid);
  const code = (row?.code ?? row?.clave ?? row?.codigo ?? "").toString().trim();
  const name = (
    row?.name ??
    row?.description ??
    row?.nombre ??
    row?.label ??
    row?.unit_display ??
    ""
  ).toString().trim();

  return {
    id,
    code,
    name,
    description: name,
    unit_display: row?.unit_display ?? "",
    label: getUnitLabel({
      code,
      name,
      label: row?.label,
      unit_display: row?.unit_display,
    }),
  };
}

export function normalizeDescriptionRow(row) {
  const id = extractPrimitiveId(row?.id ?? row?.pk ?? row?.uuid);
  const productId = extractPrimitiveId(
    row?.product ?? row?.product_id ?? row?.producto ?? row?.producto_id
  );
  const tenderId = extractPrimitiveId(
    row?.tender ?? row?.tender_id ?? row?.licitacion ?? row?.licitacion_id
  );
  const unitId = extractPrimitiveId(
    row?.unit ?? row?.unit_id ?? row?.unidad ?? row?.unidad_id
  );

  const unitLabel =
    row?.unit_display ||
    row?.unit_name ||
    row?.unit_label ||
    (typeof row?.unit === "object" ? getUnitLabel(row.unit) : "") ||
    "";

  const productLabel =
    row?.product_description ||
    row?.product_label ||
    (typeof row?.product === "object" ? getProductLabel(row.product) : "") ||
    "";

  return {
    id,
    productId,
    productLabel,
    tenderId,
    text: row?.text ?? row?.descripcion ?? row?.name ?? row?.label ?? "",
    estimated_unit_cost: row?.estimated_unit_cost ?? row?.costo ?? row?.cost ?? "",
    unitId,
    unitLabel,
  };
}

export function dedupeById(rows) {
  const map = new Map();

  (rows || []).forEach((row) => {
    if (!row?.id) return;
    map.set(String(row.id), row);
  });

  return Array.from(map.values());
}

export function descCacheKey(tenderId, productId) {
  return `${String(tenderId || "")}::${String(productId || "")}`;
}

/* ───────────────────────────── Label helpers ─────────────────────────────── */

export function getObjectDisplay(raw, labelBuilder) {
  if (raw && typeof raw === "object") return labelBuilder(raw);
  return "";
}

export function buildSyntheticOption(id, label) {
  return {
    id,
    label,
    name: label,
    description: label,
  };
}

/* ───────────────────────────── Misc helpers ──────────────────────────────── */

export function numOrNull(value) {
  if (value === "" || value === null || typeof value === "undefined") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

export function isNoAplicaLabel(value = "") {
  return normalizeText(value).includes("NO APLICA");
}

export function labelFrom(
  list,
  id,
  getLabel = (row) => row.name ?? row.description ?? row.label ?? String(row.id)
) {
  const row = (list || []).find((x) => String(x.id) === String(id));
  return row ? getLabel(row) : "";
}

export function fmtMoney(value) {
  if (value === null || typeof value === "undefined" || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(2);
}

/* ✅ UX helper: acepta coma, filtra caracteres raros y limita a 2 decimales */
export function sanitizeMoneyInput(raw) {
  let s = String(raw ?? "");
  s = s.replace(",", ".");
  s = s.replace(/[^\d.]/g, "");

  const parts = s.split(".");
  if (parts.length > 2) {
    s = `${parts[0]}.${parts.slice(1).join("")}`;
  }

  const [intPart, decPart] = s.split(".");
  if (decPart !== undefined) {
    s = `${intPart}.${decPart.slice(0, 2)}`;
  }

  return s;
}

/* ✅ UX helper: normaliza a 2 decimales en blur (si es válido) */
export function normalizeMoneyInput(raw) {
  const cleaned = sanitizeMoneyInput(raw);
  if (cleaned === "") return "";

  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "";

  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  return rounded.toFixed(2);
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX");
  } catch {
    return String(iso);
  }
}

/* ✅ cache tender+product */
export function getDescTextFromCache(descCache, tenderId, productId, descId) {
  const list = descCache?.[descCacheKey(tenderId, productId)] || [];
  const found = list.find((d) => String(d.id) === String(descId));
  return found?.text || "";
}

/* ✅ Prefer backend bonito si existe; si no, usa cache/fallback; soporta manual_description */
export function getNiceDescDisplay({ row, descCache, tenderId }) {
  if (row?.description_display) return row.description_display;

  if (row?.description_text) {
    if (row?.description) return `${row.description_text} (ID: ${row.description})`;
    return row.description_text;
  }

  if (row?.manual_description) return String(row.manual_description);

  const txt = getDescTextFromCache(descCache, tenderId, row?.product, row?.description);
  if (txt) return `${txt} (ID: ${row.description})`;

  return row?.description ? `ID: ${row.description}` : "";
}

/** ✅ helper: id real para backend */
export function isRealId(value) {
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n > 0;
}